import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Eye, Edit3, Copy, Check, Code, Upload, ClipboardPaste, Wand2 } from 'lucide-react';

interface HTMLViewModalProps {
  html: string;
  onClose: () => void;
  onApply: (html: string) => void;
  title?: string;
}

function formatHtml(htmlString: string): string {
  let formatted = '';
  let indent = 0;
  const tab = '  ';

  htmlString.split(/(<[^>]+>)/g).forEach((element) => {
    if (element.match(/^<\/\w/)) {
      indent = Math.max(0, indent - 1);
    }

    if (element.trim()) {
      formatted += tab.repeat(indent) + element.trim() + '\n';
    }

    if (element.match(/^<\w[^>]*[^/]>$/)) {
      indent++;
    }
  });

  return formatted.trim();
}

export function HTMLViewModal({ html, onClose, onApply, title = 'HTML Weergave' }: HTMLViewModalProps) {
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [editedHtml, setEditedHtml] = useState(html);
  const [copied, setCopied] = useState(false);
  const [pasted, setPasted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gutterRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditedHtml(html);
  }, [html]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(mode === 'edit' ? editedHtml : html);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePasteReplace = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setEditedHtml(text);
        setPasted(true);
        setTimeout(() => setPasted(false), 2000);
      }
    } catch {
      textareaRef.current?.focus();
    }
  };

  const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        setEditedHtml(reader.result);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleFormat = () => {
    setEditedHtml(formatHtml(editedHtml));
  };

  const handleApply = () => {
    if (mode === 'edit') {
      onApply(editedHtml);
      onClose();
    }
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const target = e.currentTarget;
      const start = target.selectionStart;
      const end = target.selectionEnd;
      const next = editedHtml.slice(0, start) + '  ' + editedHtml.slice(end);
      setEditedHtml(next);
      requestAnimationFrame(() => {
        target.selectionStart = target.selectionEnd = start + 2;
      });
    }
  };

  const syncGutterScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
    if (gutterRef.current) {
      gutterRef.current.scrollTop = e.currentTarget.scrollTop;
    }
  }, []);

  const displayHtml = mode === 'view' ? formatHtml(html) : editedHtml;
  const lineCount = displayHtml.split('\n').length;
  const charCount = displayHtml.length;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[60]"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && mode === 'view') onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden animate-scale-in">
        <div className="flex justify-between items-center px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-[#231F20] rounded-xl">
              <Code className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <div>
              <h2 className="font-roca text-xl font-semibold text-[#231F20]">{title}</h2>
              <p className="text-sm text-gray-500">
                {mode === 'view'
                  ? 'Bekijk de HTML van je campagne'
                  : 'Plak, importeer of bewerk je eigen HTML'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-[#231F20] hover:bg-[#F2F2EF] rounded-xl transition-colors"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-100">
          <div className="flex bg-[#F2F2EF] rounded-xl p-1">
            <button
              onClick={() => setMode('view')}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                mode === 'view'
                  ? 'bg-[#231F20] text-white shadow-sm'
                  : 'text-gray-600 hover:text-[#231F20]'
              }`}
            >
              <Eye className="w-4 h-4" />
              Weergave
            </button>
            <button
              onClick={() => setMode('edit')}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
                mode === 'edit'
                  ? 'bg-[#231F20] text-white shadow-sm'
                  : 'text-gray-600 hover:text-[#231F20]'
              }`}
            >
              <Edit3 className="w-4 h-4" />
              Bewerken
            </button>
          </div>

          <div className="flex-1" />

          {mode === 'edit' && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".html,.htm,text/html"
                onChange={handleImportFile}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 text-sm font-medium text-[#231F20] rounded-lg hover:bg-[#F2F2EF] transition-colors flex items-center gap-1.5"
                title="Importeer een .html-bestand"
              >
                <Upload className="w-4 h-4" />
                Importeer
              </button>
              <button
                onClick={handlePasteReplace}
                className="px-3 py-2 text-sm font-medium text-[#231F20] rounded-lg hover:bg-[#F2F2EF] transition-colors flex items-center gap-1.5"
                title="Vervang de inhoud door je klembord"
              >
                {pasted ? (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-green-600">Geplakt</span>
                  </>
                ) : (
                  <>
                    <ClipboardPaste className="w-4 h-4" />
                    Plak & vervang
                  </>
                )}
              </button>
              <button
                onClick={handleFormat}
                className="px-3 py-2 text-sm font-medium text-[#231F20] rounded-lg hover:bg-[#F2F2EF] transition-colors flex items-center gap-1.5"
                title="Lijn de HTML netjes uit"
              >
                <Wand2 className="w-4 h-4" />
                Opmaken
              </button>
              <div className="w-px h-5 bg-gray-200" />
            </>
          )}

          <button
            onClick={handleCopy}
            className="px-3 py-2 text-sm font-medium text-[#231F20] rounded-lg hover:bg-[#F2F2EF] transition-colors flex items-center gap-1.5"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-green-600">Gekopieerd</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Kopieer
              </>
            )}
          </button>
        </div>

        <div className="flex-1 overflow-hidden px-6 py-4">
          <div className="h-full rounded-xl bg-[#231F20] overflow-hidden flex">
            {mode === 'view' ? (
              <pre className="flex-1 h-full text-[#F2F2EF] p-5 overflow-auto font-mono text-[13px] leading-relaxed">
                <code>{displayHtml}</code>
              </pre>
            ) : (
              <>
                <div
                  ref={gutterRef}
                  aria-hidden
                  className="py-5 pl-4 pr-3 text-right font-mono text-[13px] leading-relaxed text-white/25 select-none overflow-hidden border-r border-white/10"
                >
                  {Array.from({ length: lineCount }, (_, i) => (
                    <div key={i}>{i + 1}</div>
                  ))}
                </div>
                <textarea
                  ref={textareaRef}
                  value={editedHtml}
                  onChange={(e) => setEditedHtml(e.target.value)}
                  onKeyDown={handleTextareaKeyDown}
                  onScroll={syncGutterScroll}
                  className="flex-1 h-full bg-transparent text-[#F2F2EF] p-5 font-mono text-[13px] leading-relaxed resize-none focus:outline-none placeholder:text-white/30"
                  placeholder="Plak of typ hier je HTML..."
                  spellCheck={false}
                  autoFocus
                />
              </>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center gap-3">
          <p className="text-xs text-gray-400">
            {lineCount} regel{lineCount !== 1 ? 's' : ''} · {charCount.toLocaleString('nl-NL')} tekens
          </p>
          <div className="flex-1" />
          {mode === 'edit' ? (
            <>
              <button onClick={onClose} className="btn-secondary">
                Annuleren
              </button>
              <button onClick={handleApply} className="btn-primary">
                Toepassen
              </button>
            </>
          ) : (
            <button onClick={onClose} className="btn-secondary">
              Sluiten
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
